import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useParams } from 'react-router-dom';
import superadminServices from 'app/services/superadmin-services';
import { formatTimeAgo } from 'app/shared/devices/deviceDisplay';
import { ResponsiveDataTable, SecondaryActionButton, StandardPageHeader } from 'app/shared/ui';
import AdoptVisionDeviceDialog from './AdoptVisionDeviceDialog';
import {
  ANALYTIC_TYPE_LABELS,
  OBJECT_CLASS_LABELS,
  OnlineChip,
  PASSWORD_STATES,
  describeError,
  formatDateTime,
  formatNumber,
  formatUptime,
} from './visionDisplay';

const Section = ({ title, action = null, children }) => (
  <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="h5">{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const Field = ({ label, children, mono = false }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>{children ?? '-'}</Typography>
  </Stack>
);

const downloadJson = (filename, data) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// Detalle de un Jetson de Xpit AI Vision: estado, versiones, salud, puntos, la copia de su configuración y la contraseña
// de su interfaz. La configuración se edita en el propio equipo; aquí solo se guarda la copia.
const VisionDeviceShow = () => {
  const { id } = useParams();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [password, setPassword] = useState(null);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [showConfigJson, setShowConfigJson] = useState(false);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [companies, setCompanies] = useState([]);

  const loadDevice = useCallback(() => {
    superadminServices.getVisionDevice(id)
      .then(setDevice)
      .catch((error) => setNotice({ severity: 'error', text: describeError(error, 'No se pudo cargar el equipo') }))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  useEffect(() => {
    if (!device || device.adopted || companies.length) return;
    superadminServices.getCompanies('?page=1&include_archived=false')
      .then((resp) => setCompanies(resp?.data || []))
      .catch(() => setCompanies([]));
  }, [device, companies.length]);

  const togglePassword = async () => {
    if (password) {
      setPassword(null);
      return;
    }
    setPasswordBusy(true);
    try {
      const resp = await superadminServices.getVisionDeviceUiPassword(id);
      setPassword(resp.ui_password);
    } catch (error) {
      setNotice({ severity: 'error', text: describeError(error, 'No se pudo leer la contraseña') });
    } finally {
      setPasswordBusy(false);
    }
  };

  const copyPassword = async () => {
    try {
      const value = password || (await superadminServices.getVisionDeviceUiPassword(id)).ui_password;
      await navigator.clipboard.writeText(value);
      setNotice({ severity: 'success', text: 'Contraseña copiada.' });
    } catch (error) {
      setNotice({ severity: 'error', text: describeError(error, 'No se pudo copiar la contraseña') });
    }
  };

  const rotatePassword = async () => {
    if (!window.confirm('¿Generar una contraseña nueva? La actual deja de servir apenas el equipo reciba la nueva.')) return;
    setPasswordBusy(true);
    try {
      const resp = await superadminServices.rotateVisionDeviceUiPassword(id);
      setDevice(resp.vision_device);
      setPassword(resp.ui_password);
      setNotice({ severity: 'success', text: 'Contraseña nueva generada.' });
    } catch (error) {
      setNotice({ severity: 'error', text: describeError(error, 'No se pudo rotar la contraseña') });
    } finally {
      setPasswordBusy(false);
    }
  };

  const pushSettings = async () => {
    setPasswordBusy(true);
    try {
      setDevice(await superadminServices.pushVisionDeviceSettings(id));
      setNotice({ severity: 'success', text: 'Envío al equipo pedido.' });
    } catch (error) {
      setNotice({ severity: 'error', text: describeError(error, 'No se pudo pedir el envío') });
    } finally {
      setPasswordBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (!device) {
    return (
      <Box>
        <StandardPageHeader title="Equipo de visión" backTo="/superadmin/vision" />
        {!!notice && <Alert severity={notice.severity}>{notice.text}</Alert>}
      </Box>
    );
  }

  const health = device.health || {};
  const config = device.config || null;
  const cameras = health.cameras || [];
  const configCameras = config?.cameras || [];
  const passwordStatus = device.ui_password_status;
  const passwordState = PASSWORD_STATES[passwordStatus?.state];

  const cameraColumns = [
    { key: 'name', label: 'Cámara', render: (camera) => camera.name || camera.id },
    { key: 'id', label: 'Id', render: (camera) => camera.id },
    { key: 'fps', label: 'FPS', render: (camera) => formatNumber(camera.fps, '', 1) },
    { key: 'ok', label: 'Estado', render: (camera) => <Chip size="small" color={camera.ok ? 'success' : 'error'} variant="outlined" label={camera.ok ? 'Procesando' : 'Con error'} /> },
    { key: 'error', label: 'Error', render: (camera) => camera.error || '-' },
  ];

  const pointColumns = [
    { key: 'channel', label: 'Canal', render: (point) => point.channel },
    { key: 'name', label: 'Punto en Xpit Connect', render: (point) => point.name },
    {
      key: 'analytic',
      label: 'Analítica en el equipo',
      render: (point) => (point.analytic
        ? [point.analytic.name, ANALYTIC_TYPE_LABELS[point.analytic.type] || point.analytic.type, OBJECT_CLASS_LABELS[point.analytic.object_class] || point.analytic.object_class, point.analytic.camera].filter(Boolean).join(' · ')
        : '-'),
    },
    {
      key: 'state',
      label: 'Estado',
      render: (point) => {
        if (point.archived_at) return <Chip size="small" variant="outlined" label={`Archivado ${formatDateTime(point.archived_at)}`} />;
        if (point.analytic && point.analytic.enabled === false) return <Chip size="small" color="warning" variant="outlined" label="Desactivada en el equipo" />;
        return <Chip size="small" color="success" variant="outlined" label="Activo" />;
      },
    },
    { key: 'code', label: 'Código', render: (point) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{point.code}</Typography> },
  ];

  const configCameraColumns = [
    { key: 'name', label: 'Cámara', render: (camera) => camera.name },
    { key: 'url', label: 'Fuente', render: (camera) => <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{camera.source?.url || '-'}</Typography> },
    { key: 'fps', label: 'FPS', render: (camera) => camera.fps || '-' },
    { key: 'enabled', label: 'Activa', render: (camera) => (camera.enabled === false ? 'No' : 'Sí') },
  ];

  return (
    <Box>
      <StandardPageHeader
        title={device.name || `Equipo ${device.device_id}`}
        subtitle={device.adopted ? [device.company?.name, device.store?.name].filter(Boolean).join(' · ') : 'Pendiente de adopción'}
        backTo={device.adopted ? '/superadmin/vision?state=adopted' : '/superadmin/vision'}
        backLabel="Equipos de visión"
        actions={(
          <>
            <SecondaryActionButton startIcon={<RefreshOutlinedIcon />} onClick={loadDevice}>Actualizar</SecondaryActionButton>
            {device.adopted ? (
              <Button variant="contained" size="small" sx={{ minHeight: 42 }} href={device.ui_url} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewOutlinedIcon />}>
                Abrir interfaz
              </Button>
            ) : (
              <Button variant="contained" size="small" sx={{ minHeight: 42 }} onClick={() => setAdoptOpen(true)}>Adoptar</Button>
            )}
          </>
        )}
      />

      {!!notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Section title="Estado" action={<OnlineChip device={device} />}>
            <Field label="ID" mono>{device.device_id}</Field>
            <Field label="Último estado">{`${formatDateTime(device.last_seen_at)} (${formatTimeAgo(device.last_seen_at) || '-'})`}</Field>
            <Field label="Encendido hace">{formatUptime(device.uptime_sec)}</Field>
            <Field label="MAC" mono>{device.mac}</Field>
            {!device.adopted && <Field label="Código de adopción" mono>{device.adoption_code}</Field>}
            {device.adopted && <Field label="Adoptado">{`${formatDateTime(device.adopted_at)}${device.adopted_by ? ` por ${device.adopted_by.name}` : ''}`}</Field>}
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Red">
            <Field label="IP local">{(device.local_ips || []).join(', ') || '-'}</Field>
            <Field label="Tailscale" mono>{device.tailscale_name}</Field>
            <Field label="IP Tailscale" mono>{device.tailscale_ip}</Field>
            <Field label="Interfaz">
              <a href={device.ui_url} target="_blank" rel="noopener noreferrer">{device.ui_url}</a>
              {device.tailscale_ip && (
                <> · <a href={`http://${device.tailscale_ip}:8080`} target="_blank" rel="noopener noreferrer">por IP</a></>
              )}
            </Field>
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Versiones">
            <Field label="Hardware">{device.hardware}</Field>
            <Field label="Sistema">{device.os_version}</Field>
            <Field label="L4T">{device.l4t_version}</Field>
            {Object.entries(device.components || {}).map(([component, version]) => (
              <Field key={component} label={component.replace('cl.xpit.vision.', '')}>{version}</Field>
            ))}
          </Section>
        </Grid>
        <Grid item xs={12} md={6}>
          <Section title="Salud">
            <Field label="Temperatura CPU">{formatNumber(health.cpu_temp_c, ' °C', 1)}</Field>
            <Field label="Temperatura GPU">{formatNumber(health.gpu_temp_c, ' °C', 1)}</Field>
            <Field label="Memoria usada">{formatNumber(health.mem_used_pct, ' %')}</Field>
            <Field label="Disco usado">{formatNumber(health.disk_used_pct, ' %')}</Field>
            <Field label="Modo de energía">{health.power_mode}</Field>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title="Cámaras">
            <ResponsiveDataTable columns={cameraColumns} rows={cameras} rowKey="id" emptyMessage="El equipo no informa cámaras."
              mobileTitle={(camera) => camera.name || camera.id} mobileSubtitle={(camera) => (camera.ok ? `${formatNumber(camera.fps, ' FPS', 1)}` : camera.error || 'Con error')} />
          </Section>
        </Grid>

        {device.adopted && (
          <Grid item xs={12}>
            <Section title="Contraseña de la interfaz">
              {passwordStatus ? (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{password || '••••-••••-••••-••••'}</Typography>
                    <Tooltip title={password ? 'Ocultar' : 'Mostrar'}>
                      <span><IconButton size="small" disabled={passwordBusy} onClick={togglePassword}>{password ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}</IconButton></span>
                    </Tooltip>
                    <Tooltip title="Copiar">
                      <span><IconButton size="small" disabled={passwordBusy} onClick={copyPassword}><ContentCopyOutlinedIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    {passwordState && <Chip size="small" color={passwordState.color} variant="outlined" label={passwordState.label} />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Generada {formatDateTime(passwordStatus.set_at)}
                    {passwordStatus.pushed_at ? ` · enviada ${formatDateTime(passwordStatus.pushed_at)}` : ''}
                    {passwordStatus.push_enabled ? '' : ' · el envío a los equipos está apagado hasta desplegar el stack vision en AWS'}
                  </Typography>
                  {!!passwordStatus.push_error && <Alert severity="warning">Último error al enviarla: {passwordStatus.push_error}</Alert>}
                  <Stack direction="row" spacing={1}>
                    <SecondaryActionButton disabled={passwordBusy} onClick={rotatePassword}>Rotar contraseña</SecondaryActionButton>
                    {passwordStatus.push_enabled && (
                      <SecondaryActionButton disabled={passwordBusy} onClick={pushSettings}>Reenviar al equipo</SecondaryActionButton>
                    )}
                  </Stack>
                </Stack>
              ) : (
                <Typography color="text.secondary">El equipo todavía no tiene contraseña.</Typography>
              )}
            </Section>
          </Grid>
        )}

        {device.adopted && (
          <Grid item xs={12}>
            <Section title="Puntos">
              <ResponsiveDataTable columns={pointColumns} rows={device.points || []} rowKey="id"
                emptyMessage="El equipo todavía no tiene analíticas configuradas."
                mobileTitle={(point) => `${point.channel} · ${point.name}`} mobileSubtitle={(point) => point.code} />
            </Section>
          </Grid>
        )}

        <Grid item xs={12}>
          <Section
            title="Copia de la configuración"
            action={config && (
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setShowConfigJson((value) => !value)}>{showConfigJson ? 'Ocultar JSON' : 'Ver JSON'}</Button>
                <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => downloadJson(`xpit-vision-${device.device_id}-config-v${config.version}.json`, config)}>
                  Descargar
                </Button>
              </Stack>
            )}
          >
            {config ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Versión {config.version} · {configCameras.length} {configCameras.length === 1 ? 'cámara' : 'cámaras'} · {(config.analytics || []).length} analíticas.
                  Se edita en la interfaz del equipo; aquí queda la última copia que reportó, sin contraseñas.
                </Typography>
                <ResponsiveDataTable columns={configCameraColumns} rows={configCameras} rowKey="id" emptyMessage="Sin cámaras."
                  mobileTitle={(camera) => camera.name} mobileSubtitle={(camera) => camera.source?.url} />
                <Collapse in={showConfigJson}>
                  <Box component="pre" sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflow: 'auto', fontSize: 12, maxHeight: 420 }}>
                    {JSON.stringify(config, null, 2)}
                  </Box>
                </Collapse>
              </>
            ) : (
              <Typography color="text.secondary">El equipo todavía no reportó su configuración.</Typography>
            )}
          </Section>
        </Grid>
      </Grid>

      <AdoptVisionDeviceDialog
        open={adoptOpen}
        onClose={() => setAdoptOpen(false)}
        device={device}
        companies={companies}
        onAdopted={(result) => setDevice(result.vision_device)}
      />
    </Box>
  );
};

export default VisionDeviceShow;
